/** @jsxImportSource @emotion/react */

import { ButtonStyled, ModalActionsStyled, ModalStyled, useWindowWidth } from '@nike/nike-design-system-components';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';

import UserForm from './UserForm';
import UserManagementErrorSnackbar from '../UserManagementErrorSnackbar';
import UserManagementLoadingAnimation from '../UserManagementLoadingAnimation';

import { TranslatedCopy } from '../../intl/TranslatedCopy';
import { requestsActions } from '../../requests/actions';
import { requestsSelectors } from '../../requests/reducer';
import { usersActions } from './actions';
import { usersSelectors } from './reducer';

import { formatDateTimestamp } from '../../util/date';

const UserModal = () => {
  const dispatch = useDispatch();

  const selectedUserEmail = useSelector(usersSelectors.selectedUserEmail);
  const selectedUserName = useSelector(usersSelectors.selectedUserName);
  const isModalOpen = useSelector(usersSelectors.isModalOpen);
  const selectedUserIsActive = useSelector(usersSelectors.selectedUserIsActive);
  const selectedUserLastLogin = useSelector(usersSelectors.selectedUserLastLogin);
  const updateUserError = useSelector(requestsSelectors.updateUserError);
  const updateUserSuccess = useSelector(requestsSelectors.updateUserSuccess);
  const selectedUserIsLocked = useSelector(usersSelectors.selectedUserIsLocked);
  // eslint-disable-next-line no-nested-ternary
  const accountStatus = selectedUserIsActive ? (selectedUserIsLocked ? 'locked' : 'active') : 'deactivated';

  const isMobile = useWindowWidth() < 769;

  useEffect(() => {
    if (updateUserSuccess) {
      dispatch({ type: usersActions.closeModal.name });
    }
  }, [updateUserSuccess]);

  return (
    <ModalStyled
      appearance={isMobile ? 'fullscreen' : 'base'}
      closeModal={() => {
        dispatch({ type: usersActions.closeModal.name });

        if (updateUserError) {
          dispatch({ type: requestsActions.clearUpdateUserError.name });
        }
      }}
      css={{
        '@media (min-width: 769px)': {
          borderRadius: '8px',
          maxWidth: '1010px'
        }
      }}
      isOpen={isModalOpen}
      slots={{
        FooterSlot: (
          <ModalActionsStyled>
            <ButtonStyled disabled={updateUserError} form='userUpdate' type='submit'>
              <TranslatedCopy id='userManagement.button.active' />
            </ButtonStyled>
          </ModalActionsStyled>
        )
      }}
      title={new TranslatedCopy({ id: 'userManagement.details' }).toString()}
      modalHeaderCompProps={{ style: { paddingBottom: '20px' } }}
      modalContentCompProps={{ style: { overflowY: 'scroll' } }}
    >
      <div css={{ display: 'flex', flexDirection: 'row' }}>
        <div css={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div>
            <span css={{ fontWeight: 500 }}>
              <TranslatedCopy id='global.table.name' />:
            </span>{' '}
            {selectedUserName}
          </div>
          <div>
            <span css={{ fontWeight: 500 }}>
              <TranslatedCopy id='global.form.email' />:
            </span>{' '}
            {selectedUserEmail}
          </div>
        </div>
        <div css={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div>
            <span css={{ fontWeight: 500 }}>
              <TranslatedCopy id='userManagement.table.status' />:
            </span>{' '}
            <TranslatedCopy id={`userManagement.status.${accountStatus}`} />
          </div>
          <div css={{ flex: 1 }}>
            <span css={{ fontWeight: 500 }}>
              <TranslatedCopy id='userManagement.table.lastLogin' />:
            </span>{' '}
            {formatDateTimestamp(selectedUserLastLogin)}
          </div>
        </div>
      </div>
      <UserForm />
      <UserManagementErrorSnackbar />
      <UserManagementLoadingAnimation />
    </ModalStyled>
  );
};

export default UserModal;
